// src/components/devices/SmartLockCard.js
import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Lock, Unlock, Battery, Wifi, WifiOff } from 'lucide-react';
import LockControl from './LockControl';
import { motion } from 'framer-motion';
import { isLockedOf } from '../../lib/lockState';

/**
 * Smart Lock Card - Apple/Tesla style perception fix
 *
 * Core idea:
 * - Immediately show a "processing" state on tap (locking/unlocking)
 * - Disable repeat taps while waiting for the real lock to update (3–7s)
 * - Auto-clear after a timeout if the lock never updates
 */
const ACTION_TIMEOUT_MS = 8000;

const SmartLockCard = ({ lock, onToggle, onDetails }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [justToggled, setJustToggled] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'locking' | 'unlocking' | null
  const expectedLockedRef = useRef(null); // boolean | null
  const timeoutRef = useRef(null);

  const isLocked = isLockedOf(lock);
  const availabilityStatus = lock.availabilityStatus || 'UNKNOWN';
  const isOffline = availabilityStatus !== 'AVAILABLE';
  const battery = lock.batteryLevel !== undefined ? lock.batteryLevel : lock.battery;

  const clearPending = () => {
    setPendingAction(null);
    expectedLockedRef.current = null;
    setIsLoading(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // When websocket / state updates reach the expected state, end the processing UI.
  useEffect(() => {
    const expected = expectedLockedRef.current;
    if (expected === null) return;

    if (isLocked === expected) {
      clearPending();
      setJustToggled(true);
      const t = setTimeout(() => setJustToggled(false), 1000);
      return () => clearTimeout(t);
    }
  }, [isLocked]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Handle lock toggle with immediate feedback
  const handleToggle = async (action) => {
    if (isOffline) return;
    if (isLoading || pendingAction) return;

    const nextLocked = action === 'lock';
    expectedLockedRef.current = nextLocked;

    // Immediate perception fix (no more "stuck")
    setPendingAction(nextLocked ? 'locking' : 'unlocking');
    setIsLoading(true);

    // Safety net: if no websocket update arrives, exit "stuck" mode after a fixed time.
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      // Keep calm: allow retry without scary error UI.
      clearPending();
      // (Optional) You can add a small toast here later. Keeping minimal changes.
      // console.warn('Lock action taking longer than usual');
    }, ACTION_TIMEOUT_MS);

    try {
      // Do NOT await for UX reasons? We still await, but UI already updated instantly.
      // Also: we do NOT clear loading here; we clear when websocket state matches (or timeout).
      if (action === 'lock') {
        await onToggle(lock.lockMac, false);
      } else {
        await onToggle(lock.lockMac, true);
      }
    } catch (error) {
      console.error('Error toggling lock:', error);
      clearPending();
    }
  };

  // Handle details click
  const handleDetails = (e) => {
    e?.stopPropagation();
    if (onDetails && !isLoading && !pendingAction) {
      onDetails(lock);
    }
  };

  // Get state-based styling
  const getCardStyle = () => {
    if (isOffline) {
      return {
        background: 'hsl(var(--card))',
        opacity: 0.6
      };
    }

    if (isLocked) {
      return {
        background: 'linear-gradient(135deg, hsl(var(--card)) 0%, hsl(142, 71%, 45%, 0.05) 100%)',
        borderColor: 'hsl(142, 71%, 45%, 0.2)'
      };
    }

    return {
      background: 'linear-gradient(135deg, hsl(var(--card)) 0%, hsl(0, 84%, 60%, 0.05) 100%)',
      borderColor: 'hsl(0, 84%, 60%, 0.2)'
    };
  };

  // Get lock icon based on state
  const getLockIcon = () => {
    if (isOffline) {
      return <Lock className="h-6 w-6 text-muted-foreground" />;
    }
    return isLocked ? (
      <Lock className="h-6 w-6 text-green-600" />
    ) : (
      <Unlock className="h-6 w-6 text-red-600" />
    );
  };

  // Get battery icon
  const getBatteryIcon = () => {
    if (battery === undefined) return null;
    if (battery < 20) {
      return <Battery className="h-4 w-4 text-red-500" />;
    }
    return <Battery className="h-4 w-4 text-green-600" />;
  };

  // Get connectivity icon
  const getConnectivityIcon = () => {
    return isOffline ? (
      <WifiOff className="h-4 w-4 text-muted-foreground" />
    ) : (
      <Wifi className="h-4 w-4 text-muted-foreground" />
    );
  };

  const statusText = isOffline
    ? 'Offline'
    : pendingAction === 'unlocking'
    ? 'Unlocking…'
    : pendingAction === 'locking'
    ? 'Locking…'
    : isLocked
    ? 'Locked'
    : 'Unlocked';

  return (
    <motion.div
      initial={false}
      animate={{
        scale: justToggled ? 1.02 : 1,
        boxShadow: justToggled
          ? isLocked
            ? '0 0 20px hsl(142, 71%, 45%, 0.4)'
            : '0 0 20px hsl(0, 84%, 60%, 0.4)'
          : '0 1px 3px hsl(var(--shadow))'
      }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <Card
        className={`
          relative overflow-hidden cursor-pointer
          transition-all duration-200
          hover:scale-[0.98] active:scale-[0.96]
          ${isOffline ? 'opacity-60' : ''}
        `}
        style={getCardStyle()}
        onClick={handleDetails}
      >
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-3 w-full">
              {getLockIcon()}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-medium truncate">
                  {lock.lockAlias || `Lock (${lock.lockMac})`}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {statusText}
                  {!pendingAction && battery !== undefined ? ` • ${battery}%` : ''}
                </p>
              </div>
            </div>

            <div className="relative">
              {!isOffline ? (
                <>
                  <LockControl
                    isLocked={isLocked}
                    onLock={() => handleToggle('lock')}
                    onUnlock={() => handleToggle('unlock')}
                    disabled={isLoading || !!pendingAction}
                    processing={pendingAction}
                    className="w-20 h-20"
                  />

                  {/* Success glow */}
                  {justToggled && (
                    <motion.div
                      className={`absolute -inset-2 rounded-full ${
                        isLocked ? 'bg-green-400/30' : 'bg-red-400/30'
                      } blur-md`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    />
                  )}
                </>
              ) : (
                <div className="w-20 h-20 rounded-full bg-muted border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                  <WifiOff className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
            </div>

            {!pendingAction && (
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                {battery !== undefined && (
                  <div className="flex items-center gap-1">
                    {getBatteryIcon()}
                    <span>{battery}%</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  {getConnectivityIcon()}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SmartLockCard;
